import React, { useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../lib/api/auth';
import { User, Shield, Save, CheckCircle2, AlertCircle } from 'lucide-react';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { ChangePasswordForm } from '../features/auth/components/ChangePasswordForm';

export const SettingsPage: React.FC = () => {
    const { user, updateUser, logout } = useAuthStore();

    // Profile State
    const [name, setName] = useState(user?.name || '');
    const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl || '');
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

    // Status & Tab State
    const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [activeTab, setActiveTab] = useState<'profile' | 'security'>('profile');

    // Modal State
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        variant: 'danger' | 'warning' | 'info';
        confirmText?: string;
        cancelText?: string;
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        variant: 'warning',
        onConfirm: () => { },
    });

    const showConfirmation = (
        title: string,
        message: string,
        onConfirm: () => void,
        variant: 'danger' | 'warning' | 'info' = 'warning',
        confirmText = 'Confirm'
    ) => {
        setModalConfig({
            isOpen: true,
            title,
            message,
            variant,
            onConfirm,
            confirmText,
            cancelText: 'Cancel'
        });
    };

    const handleUpdateProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsUpdatingProfile(true);
        setStatus(null);

        try {
            const response = await authService.updateProfile({ name, avatarUrl });
            if (response.success) {
                updateUser({ name, avatarUrl });
                setStatus({ type: 'success', message: 'Profile updated successfully!' });
                setTimeout(() => setStatus(null), 3000);
            }
        } catch (error: any) {
            setStatus({ type: 'error', message: error.response?.data?.message || 'Failed to update profile' });
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    const handleLogout = () => {
        showConfirmation(
            'Sign Out',
            'Are you sure you want to sign out?',
            () => logout(),
            'danger',
            'Sign Out'
        );
    };

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-5xl mx-auto w-full overflow-hidden">
            {/* Header - Simplified */}
            <div className="flex-shrink-0 flex items-center justify-end mb-6">
                <button
                    onClick={handleLogout}
                    className="btn-secondary !px-3 !py-1.5 !text-xs !text-red-600 hover:!bg-red-50 hover:!border-red-200"
                >
                    Sign Out
                </button>
            </div>

            {/* Status Message (Profile-level) */}
            {status && (
                <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 animate-fade-in ${status.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                    {status.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                    <p className="text-sm font-medium">{status.message}</p>
                </div>
            )}

            {/* Tabs */}
            <div className="flex-shrink-0 bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden mb-6 flex divide-x divide-neutral-100">
                {[
                    { id: 'profile', label: 'Profile Settings', icon: User },
                    { id: 'security', label: 'Security & Auth', icon: Shield },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm transition-all relative group ${activeTab === tab.id
                            ? 'bg-neutral-50 font-medium text-neutral-900'
                            : 'text-neutral-500 hover:bg-neutral-50/50 hover:text-neutral-700'
                            }`}
                    >
                        <tab.icon size={16} className={activeTab === tab.id ? 'text-accent-primary' : 'text-neutral-400 group-hover:text-neutral-500'} />
                        {tab.label}
                        {activeTab === tab.id && (
                            <div className="absolute inset-x-0 bottom-0 h-0.5 bg-accent-primary" />
                        )}
                    </button>
                ))}
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-6 pb-6">

                {/* Profile Tab */}
                {activeTab === 'profile' && (
                    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden animate-slide-up">
                        <div className="p-6 space-y-8 max-w-2xl">
                            <form onSubmit={handleUpdateProfile} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Display Name</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300"
                                        placeholder="Enter your name"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-neutral-900 uppercase tracking-wide">Avatar Image URL</label>
                                    <input
                                        type="text"
                                        value={avatarUrl}
                                        onChange={(e) => setAvatarUrl(e.target.value)}
                                        className="w-full px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all placeholder:text-neutral-300"
                                        placeholder="https://example.com/avatar.png"
                                    />
                                    <p className="text-[10px] text-neutral-400">Provide a direct link to an image file.</p>
                                </div>

                                <div className="pt-4 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isUpdatingProfile}
                                        className="btn-primary !px-4 !py-2 flex items-center gap-2"
                                    >
                                        {isUpdatingProfile ? (
                                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <Save size={14} />
                                        )}
                                        {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Security Tab */}
                {activeTab === 'security' && (
                    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden animate-slide-up">
                        <div className="p-6 space-y-8 max-w-2xl">
                            <ChangePasswordForm />
                        </div>
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={modalConfig.isOpen}
                onCancel={() => setModalConfig({ ...modalConfig, isOpen: false })}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                variant={modalConfig.variant}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
        </div>
    );
};
